import { useState, useEffect, useRef } from 'react';
import { Music, Check, ArrowRight, BookOpen, Headphones, SlidersHorizontal, Calendar, Users, Building2, ClipboardCheck } from 'lucide-react';
import { API_BASE_URL } from '../config';
import './Marketing.css';

const learningGroups = [
  [BookOpen, '系统学习', '乐理学习 · 音乐简史 · 乐器百科', '从理解到练习，逐步建立音乐基础'],
  [Headphones, '基础训练', '识谱 · 指板 · 节奏 · 听音 · 声乐', '课后有方向，练习有反馈'],
  [SlidersHorizontal, '实用工具', '节拍器 · 吉他调音', '做好练习准备，随时进入状态'],
];
const management = [
  [Calendar, '排课与约课', '连接教师、教室与学员，灵活安排一对一及跨校区课程。'],
  [ClipboardCheck, '报名与课消', '串联报名、课次与上课记录，减少重复核对。'],
  [Users, '师资管理', '适配全职与兼职师资，按机构规则管理课时结算。'],
  [Building2, '校区与经营', '汇总经营数据，按岗位与校区分配操作权限。'],
];
function goToForm(event) {
  event.preventDefault();
  document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  document.getElementById('userName')?.focus({ preventScroll: true });
}
const VERSION_META = {
  BASIC: { stage: '单校区起步', audience: '适合个人工作室与单校区机构' },
  STANDARD: { stage: '多校区成长', audience: '适合多校区连锁培训机构' },
  FRANCHISE: { stage: '加盟体系', audience: '适合品牌总部与加盟管理' },
};

function HomePage() {
  const [formData, setFormData] = useState({
    userName: '',
    mobile: '',
    companyName: ''
  });

  const [message, setMessage] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const submitLock = useRef(false);

  // 订阅版本数据
  const [editions, setEditions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(true);
  const [versionsError, setVersionsError] = useState(null);
  const [versionsRetry, setVersionsRetry] = useState(0);

  useEffect(() => {
    let active = true;
    setVersionsLoading(true);
    setVersionsError(null);
    fetch(`${API_BASE_URL}/rest/subscription_intro/get`)
      .then((resp) => resp.json())
      .then((result) => {
        if (!active) return;
        if (result.code === '0' && result.data?.editions) {
          setEditions(result.data.editions);
        } else {
          setVersionsError(result.msg || '加载失败');
        }
      })
      .catch((err) => {
        if (!active) return;
        console.error('加载订阅版本失败', err);
        setVersionsError('网络异常，请稍后重试');
      })
      .finally(() => { if (active) setVersionsLoading(false); });
    return () => { active = false; };
  }, [versionsRetry]);

  // 滚动入场动画：进入视口时为 .reveal 元素追加 .is-visible
  useEffect(() => {
    const els = Array.from(document.querySelectorAll('.marketing .reveal'));
    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      }),
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [versionsLoading, versionsError, editions]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitLock.current) return;

    const mobileRegex = /^1[3-9]\d{9}$/;
    if (!mobileRegex.test(formData.mobile)) {
      showMessage('请输入正确的中国大陆手机号码', 'error');
      return;
    }

    submitLock.current = true;
    setSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/add_application`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();
      if (result.code === '0') {
        showMessage('提交成功！我们会尽快与您联系。', 'success');
        setFormData({
          userName: '',
          mobile: '',
          companyName: ''
        });
      } else {
        showMessage('提交失败，请稍后重试。', 'error');
      }
    } catch (error) {
      console.error('Error:', error);
      showMessage('提交失败，请检查网络连接后重试。', 'error');
    } finally {
      submitLock.current = false;
      setSubmitting(false);
    }
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });

  };

  return (
    <div className="marketing" onClick={event => { if (event.target.closest('a[href="#contact"]')) goToForm(event); }}>
      <header className="marketing-header"><a className="brand" href="#top"><img className="brand-logo" src="/favicon.svg" alt="" width="32" height="32" />申嘻教务</a><nav aria-label="页面导航"><a href="#features">员工端</a><a href="#students">学员端</a><a href="#music">音乐空间</a><a href="#versions">订阅方案</a><a className="management-entry" href="https://mc.sanesee.com/">机构管理端</a><a className="primary" href="#contact">免费申请试用</a></nav></header>
      <main id="top">
        <section className="marketing-hero">
          <div className="hero-content container">
          <div className="reveal"><p className="eyebrow">专为音乐培训机构打造</p><h1>管好教务，<br/><em>也陪伴学员进步</em></h1><p className="lead">排课、约课、课消与课后练习，<br/>一站连接机构管理与学员学习。</p><div className="actions"><a className="primary" href="#contact">免费申请试用 <ArrowRight size={18}/></a><a className="secondary" href="#music">了解音乐空间</a></div><p className="hero-note">从个人工作室，到多校区音乐机构</p>
          </div>
            <aside className="official-account" aria-label="官方公众号">
              <img src="/images/marketing/wechat-official-qrcode.jpg" alt="申嘻教务官方公众号二维码" width="120" height="120" />
              <div><strong>申嘻教务官方公众号</strong><p>扫码关注，与我们联系。</p></div>
            </aside>
          </div>
        </section>
        <section className="management-section" id="features"><div className="container"><p className="eyebrow reveal">教务管理 / 机构日常协作</p><h2 className="reveal">把繁杂事务，<br className="mobile-break"/>留给有序的流程</h2><div className="management-showcase"><figure className="work-preview reveal reveal-d1"><img src="/images/marketing/employee-work.png" alt="员工端工作页：招生管理与教务管理功能入口" width="390" height="844" loading="lazy"/><figcaption>员工端 · 工作</figcaption></figure><div className="management-grid reveal reveal-d2">{management.map(([Icon,title,description])=><article key={title}><Icon aria-hidden="true"/><h3>{title}</h3><p>{description}</p></article>)}</div></div></div></section>
      <section className="student-showcase container" id="students"><div className="reveal"><p className="eyebrow">学员端 / 日常学习服务</p><h2>让约课与学习，<br/>触手可及</h2><p className="section-intro">预约上课、查看课程、获取资源，<br/>从学员首页轻松进入。</p></div><figure className="reveal reveal-d1"><img src="/images/marketing/student-home.png" alt="学员首页：常用功能、音乐空间与上课安排" width="390" height="844" loading="lazy"/><figcaption>学员端 · 首页</figcaption></figure></section>
        <section className="marketing-section" id="music"><div className="container"><p className="eyebrow reveal">音乐空间 / 课后学习支持</p><h2 className="reveal">让课堂之外，<br className="mobile-break"/>也有学习的延续</h2><p className="section-intro reveal">学知识、练基础、用工具，帮助学员把每一次学习落到练习中。</p>
          <div className="music-presentation reveal reveal-d1"><div className="hero-visual"><div className="visual-caption"><Music size={20}/><span>课堂之外，学习继续</span></div><img src="/images/marketing/music-space.png" alt="音乐空间真实界面：课程学习、基础练习与实用工具" width="390" height="844" loading="lazy"/><span className="visual-label">学员端 · 音乐空间</span></div><div className="learning-grid">{learningGroups.map(([Icon,title,features,description])=><article key={title}><Icon aria-hidden="true"/><h3>{title}</h3><p className="feature-names">{features}</p><p>{description}</p></article>)}</div>
          </div><div className="value-strip reveal reveal-d2">{[['01','学习更有方向','课堂之外有内容可学、有基础可练。'],['02','进步更可感知','学习记录与练习反馈，让投入有所回应。'],['03','续学更有动力','延续日常学习体验，为续费沟通积累价值。']].map(([n,title,description])=><div key={n}><span>{n}</span><h3>{title}</h3><p>{description}</p></div>)}</div>
          <p className="rights reveal">音乐空间由申嘻教务免费向符合使用条件的已报名学员提供。</p></div>
        </section>
      {/* ============== 订阅版本 ============== */}
      <section className="versions-section" id="versions">
        <div className="container">
          <h2 className="section-title reveal">选择适合您的订阅版本</h2>
          <p className="section-subtitle reveal">
            从个人工作室到连锁机构，选择适合您的管理能力
          </p>
          <div className="versions-commercial reveal" aria-label="订阅定价与优惠说明">
            <div className="versions-commercial-pricing">
              <span className="versions-commercial-label">灵活定价</span>
              <strong>按校区数量与购买年限综合定价</strong>
              <span>校区越多、购买年限越长，单校区价格越优惠。</span>
            </div>
            <div className="versions-promotion">
              <span className="versions-promotion-label">限时优惠</span>
              <strong>买 2 年送 1 年 · 买 3 年送 2 年</strong>
              <span className="versions-promotion-description">优惠具有时效性，具体以咨询时的有效方案为准。</span>
            </div>
          </div>

          {versionsLoading && <div className="versions-empty">加载中…</div>}
          {!versionsLoading && versionsError && (
            <div className="versions-empty error" role="status">订阅信息暂时无法加载，不影响申请试用。<button type="button" onClick={() => setVersionsRetry(n => n + 1)}>重试</button></div>
          )}
          {!versionsLoading && !versionsError && editions.length === 0 && (
            <div className="versions-empty">暂无版本信息</div>
          )}

          {!versionsLoading && !versionsError && editions.length > 0 && (
            <div className="versions-grid reveal">
              {(() => {
                const order = ['BASIC', 'STANDARD', 'FRANCHISE'];
                const editionMap = new Map();
                editions.forEach((ed) => editionMap.set(ed.edition, ed));
                const seen = new Set();
                const hints = {
                  STANDARD: '已包含基础版全部能力，并新增以下能力',
                  FRANCHISE: '已包含标准版全部能力，并新增以下能力',
                };
                return order.map((key) => {
                  const ed = editionMap.get(key);
                  if (!ed) return null;
                  const recommended = ed.recommendFlag === 1;
                  const meta = VERSION_META[key];
                  const all = ed.capabilities || [];
                  const diff = all.filter((cap) => {
                    const k = cap.id || cap.title;
                    if (!k) return false;
                    if (seen.has(k)) return false;
                    seen.add(k);
                    return true;
                  });
                  return (
                    <article
                      key={ed.id || ed.edition}
                      className={recommended ? 'version-card recommended' : 'version-card'}
                    >
                      <header className="version-heading">
                        <div className="version-labels">
                          <span className="version-stage">{meta.stage}</span>
                          {recommended && <span className="ribbon">推荐</span>}
                        </div>
                        <h3 className="version-name">{ed.displayName}</h3>
                        <div className="version-subtitle">{ed.subtitle || meta.audience}</div>
                      </header>

                      <ul className="capability-list">
                        {hints[key] && (
                          <li className="capability-hint">{hints[key]}</li>
                        )}
                        {diff.map((cap) => (
                          <li className="capability-item" key={cap.id || cap.title}>
                            <span className="check-icon">
                              <Check size={16} strokeWidth={2.5} />
                            </span>
                            <div className="cap-text">
                              <div className="cap-title">{cap.title}</div>
                              {cap.description && (
                                <div className="cap-desc">{cap.description}</div>
                              )}
                            </div>
                          </li>
                        ))}
                        {diff.length === 0 && (
                          <li className="capability-empty">— 暂无额外能力 —</li>
                        )}
                      </ul>

                      <a href="#contact" className="btn-cta">
                        免费申请试用
                      </a>
                    </article>
                  );
                });
              })()}
            </div>
          )}
        </div>
      </section>

      <section className="contact" id="contact">
        <div className="container">
          <div className="contact-content">
            <div className="contact-info reveal">
              <p className="eyebrow">开启机构的下一步</p>
              <h2>让管理更从容，<br />让学习有延续</h2>
              <p className="contact-intro">从日常教务到课后学习，<br />一起了解适合您机构的使用方式。</p>
              <div className="contact-highlights">
                <div><Calendar size={20} aria-hidden="true" /><div><strong>教务协作更有序</strong><p>排课、约课、报名与课消</p></div></div>
                <div><BookOpen size={20} aria-hidden="true" /><div><strong>课后学习有延续</strong><p>课程学习、基础练习与实用工具</p></div></div>
              </div>
            </div>
            <div className="contact-form-wrapper reveal reveal-d1">
              <div className="contact-form">
                <h3>免费申请试用</h3>
                <p className="contact-form-intro">留下联系方式，我们将与您沟通机构需求。</p>
                {message && (
                  <div className={`message ${message.type}`} role="alert">
                    {message.text}
                  </div>
                )}
                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label htmlFor="userName">您的姓名</label>
                    <input
                      type="text"
                      id="userName"
                      name="userName"
                      placeholder="请输入您的姓名"
                      required
                      autoComplete="name"
                      value={formData.userName}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="mobile">联系电话</label>
                    <input
                      type="tel"
                      id="mobile"
                      name="mobile"
                      placeholder="请输入手机号码"
                      required
                      autoComplete="tel"
                      inputMode="numeric"
                      value={formData.mobile}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="companyName">机构名称</label>
                    <input
                      type="text"
                      id="companyName"
                      name="companyName"
                      placeholder="请输入培训机构名称"
                      required
                      autoComplete="organization"
                      value={formData.companyName}
                      onChange={handleInputChange}
                    />
                  </div>
                  <button
                    type="submit"
                    className="btn primary"
                    disabled={submitting}
                  >
                    {submitting ? '提交中...' : '免费申请试用'}
                  </button>
                </form>
                <p className="privacy">信息仅用于试用申请与顾问联系。</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      </main><footer className="marketing-footer container"><div><strong>申嘻教务</strong><p>连接机构管理与学员学习</p></div><nav aria-label="页脚导航"><a href="#music">音乐空间</a><a href="#contact">免费申请试用</a></nav><p>© {new Date().getFullYear()} 深圳申嘻科技有限公司</p><a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">粤ICP备19098413号-2</a></footer>
    </div>
  );
}
export default HomePage;
